/**
 * @typedef {{ day_of_week: number, is_open: boolean, open_time: string, close_time: string }} ShopHour
 * @typedef {{ start_date: string, end_date: string }} Holiday
 * @typedef {{ id: string, booking_date: string, status: string, total_duration?: number }} BookingSlot
 */

/**
 * 특정 날짜의 예약 가능 시간 슬롯 계산 (30분 간격)
 * - 기존 예약과의 실제 시간 겹침 여부로 판단 (시작 시간 단순 비교 X)
 * - bufferMins: 예약과 예약 사이 휴식 시간 (기존 예약 종료 후 추가 블록)
 *
 * @param {{
 *   date: string,
 *   shopHours: ShopHour[],
 *   holidays: Holiday[],
 *   existingBookings: BookingSlot[],
 *   excludeBookingId?: string,
 *   durationMins?: number,
 *   bufferMins?: number,
 * }} params
 * @returns {string[]} HH:MM 형식 슬롯 배열
 */
export function computeAvailableSlots({
  date,
  shopHours,
  holidays,
  existingBookings,
  excludeBookingId = null,
  durationMins = 60,
  bufferMins = 0,
  lunchBreak = null,
}) {
  if (!date) return [];

  const dayOfWeek  = new Date(date).getDay();
  const daySetting = shopHours.find(h => h.day_of_week === dayOfWeek);

  if (!daySetting || !daySetting.is_open) return [];
  if (holidays.some(h => date >= h.start_date && date <= h.end_date)) return [];

  const slots   = [];
  let current   = new Date(`${date}T${daySetting.open_time || '09:00:00'}`);
  const dayEnd  = new Date(`${date}T${daySetting.close_time || '18:00:00'}`);

  while (current < dayEnd) {
    const slotEnd = new Date(current.getTime() + durationMins * 60_000);
    if (slotEnd > dayEnd) break;

    if (lunchBreak?.enabled) {
      const lbStart = new Date(`${date}T${lunchBreak.start}:00`);
      const lbEnd   = new Date(`${date}T${lunchBreak.end}:00`);
      if (current < lbEnd && slotEnd > lbStart) {
        current.setMinutes(current.getMinutes() + 30);
        continue;
      }
    }

    const hasConflict = existingBookings.some(b => {
      if (b.id === excludeBookingId) return false;
      if (b.status === 'cancelled')  return false;
      const bStart = new Date(b.booking_date);
      // existing booking blocks: start → end + buffer
      const bEnd   = new Date(bStart.getTime() + ((b.total_duration || 60) + bufferMins) * 60_000);
      // overlap: new slot starts before existing effective end AND ends after existing start
      return current < bEnd && slotEnd > bStart;
    });

    if (!hasConflict) slots.push(current.toTimeString().slice(0, 5));
    current.setMinutes(current.getMinutes() + 30);
  }

  return slots;
}
